/**
 * STY-625: was a blur a real edit, or just Quill normalizing what it was fed?
 *
 * Quill always hands back `<p>`-wrapped, entity-encoded HTML, so for a field
 * stored as plain text the value at blur never string-equals the stored one.
 * Merely focusing and leaving a field therefore looked like an edit — and the
 * save that fired posted the tab's ENTIRE in-memory document, which is how a
 * tab holding fifteen-minute-old state wiped all of submission 382708's image
 * placements.
 *
 * Compare through a canonical form instead: wrap bare text in a paragraph the
 * way Quill would, let the DOM normalize entities and attribute quoting on
 * both sides, and collapse whitespace runs (Quill collapses them when it
 * loads a value; stored text and textareas keep them). The canonical form is
 * for COMPARISON ONLY — what gets saved is untouched.
 */
const canonical = (value) => {
  const html = value == null ? '' : String(value).trim()

  // Quill's empty-editor sentinel and genuinely blank values are the same
  // "nothing" — updateInfoForSave already stores '' for the sentinel.
  if (html === '' || html === '<p><br></p>') return ''

  // Only wrap values that are not already markup. The tag check requires a
  // letter after `<` so prose that happens to start with "<3" is treated as
  // text, exactly as the HTML parser will treat it.
  const wrapped = /^<[a-zA-Z]/.test(html) ? html : `<p>${html}</p>`

  const div = document.createElement('div')
  div.innerHTML = wrapped
  return div.innerHTML.replace(/\s+/g, ' ').trim()
}

/** For Quill-backed fields: formatting changes still count as edits. */
export const richTextUnchanged = (a, b) => canonical(a) === canonical(b)

// The plain-textarea branches render the stored value with tags stripped —
// that strip is the ONLY transformation between what is stored and what the
// editor sees, so it is the only one the comparison may apply. A textarea
// preserves whitespace faithfully (unlike Quill), which means a whitespace
// edit — a newline between sentences, deliberate spacing — is a real edit;
// running these values through canonical() would collapse it away and the
// change would silently never save.
//
// Deliberately the same regex the render uses, so the two cannot drift.
const stripTags = (value) => (value == null ? '' : String(value))
  .replace(/<[^>]*>?/gm, '')

/**
 * For plain-text fields rendered from possibly-tagged stored values.
 *
 * The question is "does what is in the box equal what the box would show for
 * the stored value", so the strip goes on the STORED side only. It is a
 * one-directional transformation and applying it to the typed value as well
 * destroys real input: the regex needs no closing `>`, so a single `<`
 * swallows the rest of the field. Typing "<3" into an empty box, or "seats
 * <200" after unchanged text, stripped down to exactly what was stored, read
 * as no change, and never saved.
 */
export const plainTextUnchanged = (typed, stored) => typed === stripTags(stored)
